import React, { useState, useEffect } from "react";
import axios from 'axios';
import {
  Container,
  Box,
  Button,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Grid,
  Paper,
  Divider,
  LinearProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent
} from "@mui/material";
import {
  Timeline,
  CalendarToday,
  Search,
  Save
} from "@mui/icons-material";
import SharedNavbar from "./performancenavbar.jsx";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ViewImprovementPlans = () => {
  const [improvementPlans, setImprovementPlans] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedPipId, setExpandedPipId] = useState(null);
  const [newProgressTaskCompletions, setNewProgressTaskCompletions] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [updatedPlans, setUpdatedPlans] = useState({});
  const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

  const handleEmployeeIdChange = (e) => setEmployeeId(e.target.value);

  const fetchImprovementPlans = async (id = null) => {
    const searchId = id || employeeId;

    if (!searchId.trim()) {
      openSnackbar("Please enter an Employee ID", "warning");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/improvement-plans?employee_id=${searchId}`, {
         withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // âœ… Parse improvementAreas if it's a string
      const plansWithParsedAreas = response.data.map(plan => ({
        ...plan,
        improvementAreas: typeof plan.improvementAreas === 'string' 
          ? JSON.parse(plan.improvementAreas || '[]') 
          : (plan.improvementAreas || [])
      }));
      
      setImprovementPlans(plansWithParsedAreas);
      setUpdatedPlans({});
      setNewProgressTaskCompletions({});

      if (plansWithParsedAreas.length > 0) {
        setExpandedPipId(plansWithParsedAreas[0].id);
      }

      openSnackbar(plansWithParsedAreas.length ?
        `Found ${plansWithParsedAreas.length} improvement plans` :
        "No improvement plans found",
        plansWithParsedAreas.length ? "success" : "info"
      );
    } catch (error) {
      //console.error("Error fetching plans:", error);
      openSnackbar(error.response?.data?.message || "Failed to load plans", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
    
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/user/current?company_id=${company_id}`, {
          withCredentials: true
        });

        if (response.data && response.data.employee_id) {
          setEmployeeId(response.data.employee_id);
          setSelectedEmployee(response.data.employee_id);
          fetchImprovementPlans(response.data.employee_id);
        }
      } catch (error) {
        //console.error("Error fetching current user:", error);
        openSnackbar("Failed to fetch user information", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  const handleSaveChanges = async (planId) => {
    try {
      setLoading(true);

      const currentPlan = improvementPlans.find(p => p.id === planId);
      if (!currentPlan) {
        openSnackbar("Plan not found", "error");
        return;
      }

      const planUpdates = updatedPlans[planId] || {};
      const hasUpdates = Object.keys(planUpdates).length > 0 || newProgressTaskCompletions[planId];

      if (!hasUpdates) {
        openSnackbar("No changes to save", "warning");
        return;
      }

      const newStatus = planUpdates.status || currentPlan.status;
      const newProgress = planUpdates.progress !== undefined ? planUpdates.progress : currentPlan.progress;
      const progressNote = newProgressTaskCompletions[planId] || 'Progress updated';

      const progressUpdate = {
        date: new Date().toISOString(),
        status: newStatus,
        progress: newProgress,
        note: progressNote,
        timestamp: new Date().toISOString()
      };

      const updatedPlanData = {
        ...currentPlan,
        status: newStatus,
        progress: newProgress,
        progressUpdates: [
          ...(currentPlan.progressUpdates || []),
          progressUpdate
        ]
      };

      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.put(
        `${API_BASE_URL}/api/improvement-plans/${planId}`,
        updatedPlanData,
        {  headers: {
          Authorization: `Bearer ${token}`,"content-type": "application/json"
        } }
      );

      setImprovementPlans(prevPlans =>
        prevPlans.map(plan => 
          plan.id === planId 
            ? { ...plan, ...response.data.plan, progress: newProgress, status: newStatus }
            : plan
        )
      );

      setNewProgressTaskCompletions(prev => {
        const newState = { ...prev };
        delete newState[planId];
        return newState;
      });

      setUpdatedPlans(prev => {
        const newState = { ...prev };
        delete newState[planId];
        return newState;
      });

      openSnackbar("Changes saved successfully", "success");
    } catch (error) {
      //console.error("Error saving changes:", error);
      openSnackbar("Failed to save changes: " + (error.response?.data?.message || error.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const openSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return dayjs(dateString).tz(companyConfig.timezone).format(companyConfig.dateFormat);
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      "Completed": "success",
      "Active": "info",
      "In Progress": "info",
      "Not Started": "default",
      "Extended": "warning",
      "Terminated": "error"
    };
    return statusColors[status] || "default";
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Low': return 'success';
      default: return 'default';
    }
  };

  const clearFilters = () => {
    setSelectedEmployee("");
    setEmployeeId("");
    setImprovementPlans([]);
  };

  return (
    <>
      <SharedNavbar />
      <Container maxWidth="xl" sx={{ mt: 3 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Employee Improvement Plans Search
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Employee ID"
                  value={employeeId}
                  onChange={handleEmployeeIdChange}
                  sx={{ width: "300px" }}
                />
                <Button
                  variant="contained"
                  onClick={() => fetchImprovementPlans()}
                  startIcon={<Search />}
                  disabled={loading}
                >
                  Search
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#f5f5f5',
                borderRadius: 1,
              }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Timeline sx={{ mr: 1 }} /> Improvement Plans ({improvementPlans.length})
                </Typography>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    size="small"
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              {loading ? (
                <Box sx={{ p: 3 }}>
                  <LinearProgress />
                </Box>
              ) : (
                <List sx={{ maxHeight: '650px', overflow: 'auto', p: 0 }}>
                  {improvementPlans.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="textSecondary">No improvement plans found matching current filters</Typography>
                    </Box>
                  ) : (
                    improvementPlans.map(plan => {
                      const currentProgress = updatedPlans[plan.id]?.progress !== undefined 
                        ? updatedPlans[plan.id].progress 
                        : (plan.progress || 0);

                      return (
                        <React.Fragment key={plan.id}>
                          <ListItem
                            button
                            onClick={() => setExpandedPipId(prev => prev === plan.id ? null : plan.id)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#f9f9f9' },
                              borderLeft: `4px solid #2196f3`
                            }}
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                    Improvement Plan - {plan.employeeName || plan.firstName || plan.employee_id}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
                                    <CalendarToday fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="textSecondary">
                                      {formatDate(plan.startDate)} - {formatDate(plan.endDate)}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" color="textSecondary">
                                      Progress: {currentProgress}%
                                    </Typography>
                                    <LinearProgress
                                      variant="determinate"
                                      value={currentProgress}
                                      sx={{
                                        flexGrow: 1,
                                        height: 8,
                                        borderRadius: 5,
                                        backgroundColor: '#e0e0e0'
                                      }}
                                      color={currentProgress === 100 ? "success" : "primary"}
                                    />
                                  </Box>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Chip 
                                label={updatedPlans[plan.id]?.status || plan.status} 
                                color={getStatusColor(updatedPlans[plan.id]?.status || plan.status)} 
                              />
                            </ListItemSecondaryAction>
                          </ListItem>

                          {expandedPipId === plan.id && (
                            <Box sx={{ px: 2, pb: 2 }}>
                              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                                <Typography gutterBottom><strong>Objectives:</strong> {plan.objectives}</Typography>
                                <Typography variant="body2"><strong>Milestones:</strong> {plan.milestones || "None specified"}</Typography>

                                {/* âœ… NEW: Display Improvement Areas */}
                                {plan.improvementAreas && plan.improvementAreas.length > 0 && (
                                  <Card variant="outlined" sx={{ mt: 2, bgcolor: 'warning.50' }}>
                                    <CardContent>
                                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                        ðŸ“‹ Areas Requiring Improvement
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                        {plan.improvementAreas.map((area, idx) => (
                                          <Chip
                                            key={idx}
                                            label={`${area.area} - ${area.category}`}
                                            size="small"
                                            color={getPriorityColor(area.priority)}
                                            sx={{ fontWeight: 'medium' }}
                                          />
                                        ))}
                                      </Box>
                                      <Divider sx={{ my: 2 }} />
                                      <List dense>
                                        {plan.improvementAreas.map((area, idx) => (
                                          <ListItem key={idx}>
                                            <Box sx={{ width: '100%' }}>
                                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="body1" fontWeight="medium">
                                                  {area.area}
                                                </Typography>
                                                <Chip
                                                  label={area.priority}
                                                  size="small"
                                                  color={getPriorityColor(area.priority)}
                                                />
                                              </Box>
                                              <Typography variant="body2" color="text.secondary">
                                                Category: <strong>{area.category}</strong>
                                              </Typography>
                                            </Box>
                                          </ListItem>
                                        ))}
                                      </List>
                                    </CardContent>
                                  </Card>
                                )}

                                <Grid container spacing={2} sx={{ mt: 1 }}>
                                  <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                      <strong>Start Date:</strong> {formatDate(plan.startDate)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography variant="body2" color="textSecondary">
                                      <strong>End Date:</strong> {formatDate(plan.endDate)}
                                    </Typography>
                                  </Grid>
                                </Grid>

                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Update Progress
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                      <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                          value={updatedPlans[plan.id]?.status || plan.status || "In Progress"}
                                          onChange={(e) => setUpdatedPlans(prev => ({
                                            ...prev,
                                            [plan.id]: {
                                              ...(prev[plan.id] || {}),
                                              status: e.target.value
                                            }
                                          }))}
                                          label="Status"
                                        >
                                          {["Not Started", "In Progress", "Completed", "Extended", "Terminated"]
                                            .map(option => (
                                              <MenuItem key={option} value={option}>{option}</MenuItem>
                                            ))}
                                        </Select>
                                      </FormControl>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                      <TextField
                                        label="Progress (%)"
                                        type="number"
                                        value={updatedPlans[plan.id]?.progress !== undefined
                                          ? updatedPlans[plan.id].progress
                                          : (plan.progress || 0)}
                                        onChange={(e) => {
                                          const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                          setUpdatedPlans(prev => ({
                                            ...prev,
                                            [plan.id]: {
                                              ...(prev[plan.id] || {}),
                                              progress: value
                                            }
                                          }));
                                        }}
                                        inputProps={{ min: 0, max: 100 }}
                                        fullWidth
                                        size="small"
                                      />
                                    </Grid>
                                  </Grid>

                                  <Box sx={{ mt: 2 }}>
                                    <TextField
                                      label="Task Completion Details"
                                      multiline
                                      rows={4}
                                      value={newProgressTaskCompletions[plan.id] || ''}
                                      onChange={(e) => setNewProgressTaskCompletions(prev => ({
                                        ...prev,
                                        [plan.id]: e.target.value
                                      }))}
                                      placeholder="Enter details about tasks completed or in progress..."
                                      fullWidth
                                      variant="outlined"
                                    />
                                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                      <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<Save />}
                                        onClick={() => handleSaveChanges(plan.id)}
                                        disabled={loading}
                                      >
                                        Save Changes
                                      </Button>
                                    </Box>
                                  </Box>

                                  {plan.progressUpdates && plan.progressUpdates.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                      <Typography variant="subtitle2" gutterBottom>
                                        Progress History
                                      </Typography>
                                      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f9f9f9', maxHeight: '200px', overflow: 'auto' }}>
                                        {plan.progressUpdates?.map((update, idx) => (
                                          <Box key={idx} sx={{ mb: 2 }}>
                                            <Typography variant="caption" color="textSecondary">
                                              {formatDate(update.date)} {update.timestamp && new Date(update.timestamp).toLocaleTimeString(undefined, {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                hour12: true
                                              })}
                                            </Typography>
                                            <Typography variant="body2">{update.note}</Typography>
                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                              <Chip
                                                label={update.status}
                                                size="small"
                                                color={getStatusColor(update.status)}
                                              />
                                              <Chip
                                                label={`${update.progress || 0}%`}
                                                size="small"
                                                color={(update.progress || 0) === 100 ? "success" : "primary"}
                                              />
                                            </Box>
                                          </Box>
                                        ))}
                                      </Paper>
                                    </Box>
                                  )}
                                </Box>
                              </Paper>
                            </Box>
                          )}
                          <Divider />
                        </React.Fragment>
                      );
                    })
                  )}
                </List>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ViewImprovementPlans;